import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from './Footer';
import Header from './Header';
import { useAuth } from '../contexts/AuthContext';
import { SubscriptionPlan } from '../contexts/AuthContext';
import './PricingPage.css';

const PricingPage: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const { user, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const proCardRef = useRef<HTMLDivElement>(null);

  // 获取用户当前的订阅计划
  const currentPlan: SubscriptionPlan = user?.subscription?.plan || 'none';

  // 加载产品信息
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/order/products`);
        const data = await res.json();
        setProducts(data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 根据套餐等级返回按钮文案
  const getPlanButtonText = (planLevel: SubscriptionPlan): string => {
    if (!isLoggedIn || currentPlan === 'none') {
      return 'Select Plan';
    }

    const planLevels: SubscriptionPlan[] = ['none', 'basic', 'pro'];
    const currentIndex = planLevels.indexOf(currentPlan);
    const targetIndex = planLevels.indexOf(planLevel);

    if (currentIndex === targetIndex) {
      return 'Modify Plan';
    } else if (targetIndex > currentIndex) {
      return 'Upgrade Plan';
    } else {
      return 'Downgrade Plan';
    }
  };

  // 处理套餐按钮点击
  const handlePlanClick = async (p: any) => {
    if (!isLoggedIn) {
      // 未登录，跳转到登录页并保存当前路径
      navigate('/login', { state: { from: { pathname: '/pricing' } } });
      return;
    }

    try {
      const payload = {
        price_id: p.id
      };

      const token = localStorage.getItem('glistar_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/order/pay-order`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.status != 200) {
        alert(data.message||data.data.message);
        return;
      }

      if (data.data.sessionUrl) {
        // 跳转到 Stripe Checkout 页面
        window.location.href =  data.data.sessionUrl;
      } else {
        alert('Failed to create checkout session: ' + (data.message||data.data.message || 'Unknown error'));
        console.error('Checkout error:', data.message||data.data.message);
      }
    } catch (err: any) {
      console.error(err);
      alert('Failed to create checkout session');
    }
  };

  if (loading) return <div>Loading products...</div>;

  return (
    <div className="pricing-page">
      {/* Header */}
      <Header />

      {/* 显示支付状态提示 */}
      {statusMessage && <div className="alert alert-info">{statusMessage}</div>}

      {/* Main Content */}
      <div className="pricing-main">
        {/* Subscription Header */}
        <div className="subscription-header">
          <h1>Pick your plan</h1>
          <p>Get access to more generations and priority access to new features</p>
        </div>

        {/* Plans Section */}
        <div className="plans-section">
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button
              className={`toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingPeriod('monthly')}
            >
              Monthly
            </button>
            <button
              className={`toggle-btn ${billingPeriod === 'yearly' ? 'active' : ''}`}
              onClick={() => setBillingPeriod('yearly')}
            >
              Yearly
              <span className="discount-badge">-30%</span>
            </button>
          </div>

          {/* Plan Cards */}
          <div className="plan-cards">
            {products
              .filter(p => p.interval !== 'none' && p.interval === billingPeriod)
              .sort((a, b) => {
                // 显式定义顺序：basic 先，pro 后
                const getOrder = (name: string) => {
                  const n = name.toLowerCase();
                  if (n.includes('basic')) return 0;
                  if (n.includes('pro')) return 1;
                  return 999;
                };
                return getOrder(a.name) - getOrder(b.name);
              })
              .map((p) => (
                <div
                  key={p.id}
                  ref={p.name.toLowerCase().includes('pro') ? proCardRef : null}
                  className={`plan-card ${p.name.toLowerCase().includes('pro') ? 'featured' : ''}`}
                >
                  <div className="plan-header">
                    <h3>{p.name}</h3>
                    <div className="plan-price">
                      <span className="currency">$</span>
                      <span className="amount">{p.price_usd}</span>
                      <span className="period">/{p.interval === 'yearly' ? 'yr' : 'mo'}</span>
                    </div>
                  </div>
                  <button
                    className={`plan-button ${p.name.toLowerCase().includes('pro') ? 'primary' : 'secondary'}`}
                    onClick={() => handlePlanClick(p)}
                  >
                    {getPlanButtonText(p.name.toLowerCase() as SubscriptionPlan)}
                  </button>
                  <p className="plan-credits">{p.credits} credits/month</p>
                  <div className="plan-features">
                    {p.productsDetail?.map((detail: any) => (
                      <div className="feature enabled" key={detail.id}>
                        <span className="check">✓</span>
                        <span>{detail.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Purchase Credits Section */}
        <div className="credits-section">
          <h2>Purchase Credits</h2>
          <p className="credits-note">
            Note: Your credits will stay valid for 2 years. Please remember they can't be refunded, transferred, or used for membership.
            <br />
            📢 To purchase credits, make sure you have an active subscription.
          </p>
          <div className="credits-cards">
            {products
              .filter(p => p.interval === 'none')
              .sort((a, b) => {
                // 把 400 credits 放最前面
                if (a.credits == 400 && b.credits != 400) return -1;
                if (b.credits == 400 && a.credits != 400) return 1;
                // 其余按 credits 升序
                return a.credits - b.credits;
              })
              .map(p => (
                <div key={p.id} className={`credit-card ${p.credits == 400 ? 'featured' : ''}`}>
                  {p.credits == 400 && <div className="credit-badge">Most Popular</div>}
                  <div className="credit-info">
                    <h4>{p.credits} Credits</h4>
                    <p className="credit-price">${p.price_usd}</p>
                    <p className="credit-desc">{p.desc}</p>
                  </div>
                  <button className="credit-button" onClick={() => handlePlanClick(p)}>
                    Purchase
                  </button>
                </div>
              ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>How do I cancel my subscription?</h4>
              <p>You can cancel your subscription anytime in your account settings. After cancellation, you can still use the service until the end of your current billing cycle.</p>
            </div>
            <div className="faq-item">
              <h4>What payment methods do you support?</h4>
              <p>We support credit cards, debit cards, Stripe, and other payment methods to ensure a secure and convenient payment experience.</p>
            </div>
            <div className="faq-item">
              <h4>Can I upgrade or downgrade my plan?</h4>
              <p>Yes, you can upgrade or downgrade your plan anytime. Upgrades take effect immediately, while downgrades take effect at the next billing cycle.</p>
            </div>
            <div className="faq-item">
              <h4>What are the limitations of the free plan?</h4>
              <p>The free plan is limited to 5 face swaps per month and only supports basic AI processing quality and image formats.</p>
            </div>
            <div className="faq-item">
              <h4>How do you protect my data security?</h4>
              <p>We use enterprise-level security measures to protect your data. All uploaded files are automatically deleted after processing.</p>
            </div>
            <div className="faq-item">
              <h4>Do credits expire?</h4>
              <p>Monthly subscription credits expire at the end of each month. Purchased credits have 2 years validity upon redemption.</p>
            </div>
            <div className="faq-item">
              <h4>What's the difference between credits and monthly subscription?</h4>
              <p>Credits are perfect for occasional users who pay per use, while monthly subscription is ideal for frequent users who pay a fixed monthly fee for unlimited usage.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PricingPage;
